/**
 * Week arithmetic, in one place, because "when" is a correctness question here.
 *
 * BotGuide sprints are one week and capacity is measured per week, so every
 * task carries a `planned_week`. That value is a PLAIN CALENDAR DATE pinned to
 * a Monday - never an instant, never a timestamp.
 *
 * ## Why this file exists
 *
 * The obvious implementation - take a Date, subtract milliseconds until you
 * reach Monday - is wrong twice a year. On the second Sunday in March the
 * Denver day is 23 hours long and on the first Sunday in November it is 25, so
 * millisecond arithmetic lands on the wrong civil day for anything near a
 * boundary.
 *
 * The fix is to leave the time domain immediately. We ask Intl what the civil
 * date is in Denver, and from that point on we only do calendar arithmetic on
 * year/month/day. A calendar has no DST, so the answer cannot shift.
 *
 * Everything here is pure and total. No Date.now() except in `currentWeekStart`.
 */

/** BotGuide operates on Mountain Time. One constant, one place. */
export const BUSINESS_TIMEZONE = 'America/Denver';

/** A calendar date with no time and no zone, as `YYYY-MM-DD`. */
export type PlainDate = string;

const PLAIN_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
});

/**
 * A shape check is not enough. `Date.parse('2026-02-30')` does not fail - it
 * rolls the date over to 2 March - so a typo would be accepted here and then
 * silently mean a different day everywhere downstream. The round-trip below is
 * what actually rejects a date that does not exist.
 *
 * Years before 0100 are rejected as a side effect, because `Date.UTC` maps
 * two-digit years into the 1900s. That fails closed, which is the right
 * direction for input we have no use for.
 */
export function isPlainDate(value: unknown): value is PlainDate {
  if (typeof value !== 'string' || !PLAIN_DATE_RE.test(value)) return false;

  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;

  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

export function assertPlainDate(value: unknown): PlainDate {
  if (!isPlainDate(value)) {
    throw new TypeError(`Expected a YYYY-MM-DD calendar date, received ${JSON.stringify(value)}`);
  }
  return value;
}

/**
 * The civil date in Denver at a given instant, plus which weekday it is.
 * This is the only place an instant becomes a calendar date.
 */
export function civilDateInBusinessZone(instant: Date): {
  date: PlainDate;
  /** 0 = Monday ... 6 = Sunday. ISO ordering, not JavaScript's Sunday-first. */
  weekdayIndex: number;
} {
  const parts = partsFormatter.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): string => {
    const found = parts.find((p) => p.type === type);
    if (!found) throw new Error(`Intl did not return a "${type}" part`);
    return found.value;
  };

  const weekdayIndex = WEEKDAY_INDEX[get('weekday')];
  if (weekdayIndex === undefined) {
    throw new Error(`Unrecognised weekday "${get('weekday')}"`);
  }

  return { date: `${get('year')}-${get('month')}-${get('day')}`, weekdayIndex };
}

/** Split `YYYY-MM-DD` into numbers. */
function splitPlainDate(date: PlainDate): { y: number; m: number; d: number } {
  const [y, m, d] = assertPlainDate(date).split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

function formatPlainDate(y: number, m: number, d: number): PlainDate {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Add whole days to a calendar date.
 *
 * Date.UTC is used purely as a calendar engine. UTC has no DST, so this is
 * ordinary day counting - it never touches a local clock and never shifts.
 */
export function addDays(date: PlainDate, days: number): PlainDate {
  if (!Number.isInteger(days)) throw new TypeError(`addDays expects whole days, received ${days}`);
  const { y, m, d } = splitPlainDate(date);
  const moved = new Date(Date.UTC(y, m - 1, d + days));
  return formatPlainDate(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate());
}

/** Whole days between two calendar dates. Positive when `to` is later. */
export function daysBetween(from: PlainDate, to: PlainDate): number {
  const a = splitPlainDate(from);
  const b = splitPlainDate(to);
  const ms = Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d);
  return Math.round(ms / 86_400_000);
}

/** 0 = Monday ... 6 = Sunday, for a calendar date. */
export function weekdayIndexOf(date: PlainDate): number {
  const { y, m, d } = splitPlainDate(date);
  // getUTCDay is Sunday-first; rotate to Monday-first.
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

/**
 * The Monday of the week containing `date`.
 *
 * Pure calendar arithmetic, so a date in a DST-transition week returns the
 * same Monday as every other day in that week.
 */
export function weekStartOf(date: PlainDate): PlainDate {
  return addDays(date, -weekdayIndexOf(date));
}

/** The Monday of the week containing `instant`, as seen from Denver. */
export function weekStartOfInstant(instant: Date): PlainDate {
  const { date, weekdayIndex } = civilDateInBusinessZone(instant);
  return addDays(date, -weekdayIndex);
}

/** The Monday of the current week in Denver. */
export function currentWeekStart(now: Date = new Date()): PlainDate {
  return weekStartOfInstant(now);
}

/**
 * Today's date in Denver.
 *
 * Use this rather than `new Date().toISOString().slice(0, 10)` anywhere a
 * calendar day matters. For seven hours every evening those two disagree, and
 * the symptom is work logged after five landing on tomorrow.
 */
export function todayInBusinessZone(now: Date = new Date()): PlainDate {
  return civilDateInBusinessZone(now).date;
}

/** True when `date` is a Monday. Every `planned_week` must satisfy this. */
export function isWeekStart(date: PlainDate): boolean {
  return weekdayIndexOf(date) === 0;
}

/**
 * Snap any calendar date to its Monday, rejecting malformed input.
 * Call this on every `planned_week` at the API boundary so an off-by-a-day
 * client can never write a Tuesday into the column.
 */
export function normaliseWeekStart(date: PlainDate): PlainDate {
  return weekStartOf(assertPlainDate(date));
}

/** `count` consecutive Mondays starting at `from`. Negative counts go back. */
export function weekRange(from: PlainDate, count: number): PlainDate[] {
  const start = weekStartOf(from);
  const step = count < 0 ? -7 : 7;
  return Array.from({ length: Math.abs(count) }, (_, i) => addDays(start, i * step)).sort();
}

/** `2026-09-14` -> `Sep 14, 2026`. Display only; never parse this back. */
export function formatWeekLabel(date: PlainDate): string {
  const { y, m, d } = splitPlainDate(date);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** The `YYYY-MM` a calendar date falls in. Used for monthly invoicing. */
export function monthOf(date: PlainDate): string {
  return assertPlainDate(date).slice(0, 7);
}
